/**
 * 品牌表单组件
 * 职责: 品牌创建/编辑表单
 * 限制: <150行
 */

import React from 'react';
import { Form, Input, Switch, Row, Col } from 'antd';
import { BgColorsOutlined } from '@ant-design/icons';
import type { Brand } from '../../services/brandService';

const { TextArea } = Input;

interface BrandFormProps {
  form: any;
  editingBrand: Brand | null;
}

export const BrandForm: React.FC<BrandFormProps> = ({ form, editingBrand }) => {
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        name: '',
        archetype: '',
        tone: '',
        core_message: '',
        audience: '',
        mandatory_phrases: '',
        forbidden_words: '',
        brand_story: '',
        brand_values: '',
        primary_color: '',
        secondary_colors: '',
        logo_url: '',
        visual_style: '',
        is_active: true,
      }}
    >
      <Form.Item name="name" label="品牌名称" rules={[{ required: true, message: '请输入品牌名称' }]}>
        <Input placeholder="如：ACME" />
      </Form.Item>
      
      <Form.Item name="archetype" label="品牌原型" tooltip="如：英雄、智者、创造者等">
        <Input placeholder="参考品牌原型理论" />
      </Form.Item>
      
      <Form.Item name="tone" label="语气/风格">
        <TextArea rows={2} placeholder="品牌的语言风格和沟通语气，如：专业权威、亲切友好等" />
      </Form.Item>
      
      <Form.Item name="core_message" label="核心信息">
        <TextArea rows={3} placeholder="品牌想传达的核心价值和主张" />
      </Form.Item>
      
      <Form.Item name="audience" label="目标受众">
        <TextArea rows={2} placeholder="品牌的目标客户群体描述" />
      </Form.Item>
      
      <Form.Item name="mandatory_phrases" label="必用短语" tooltip="每行一个">
        <TextArea rows={3} placeholder="品牌文案中必须包含的短语或标语（每行一个）" />
      </Form.Item>
      
      <Form.Item name="forbidden_words" label="禁用词汇" tooltip="每行一个">
        <TextArea rows={3} placeholder="品牌文案中禁止使用的词汇（每行一个）" />
      </Form.Item>
      
      <Form.Item name="brand_story" label="品牌故事">
        <TextArea rows={3} placeholder="品牌的背景故事和发展历程" />
      </Form.Item>
      
      <Form.Item name="brand_values" label="品牌价值观">
        <TextArea rows={2} placeholder="品牌所倡导的核心价值观" />
      </Form.Item>

      <h4 style={{ marginTop: 16, marginBottom: 12, fontWeight: 600 }}>
        <BgColorsOutlined /> VI视觉规范
      </h4>
      
      <Form.Item label="主色调" tooltip="如：#FF5733">
        <Row gutter={8}>
          <Col span={18}>
            <Form.Item name="primary_color" noStyle>
              <Input placeholder="主色调颜色代码，如：#FF5733" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue }) => {
                const color = getFieldValue('primary_color');
                return (
                  <div
                    style={{
                      width: '100%',
                      height: 32,
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      backgroundColor: color || '#ffffff',
                    }}
                    title={color || '未设置'}
                  />
                );
              }}
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>
      
      <Form.Item name="secondary_colors" label="辅助色" tooltip="每行一个颜色代码">
        <TextArea rows={2} placeholder="辅助色系，每行一个颜色代码（如：#00AAFF）" />
      </Form.Item>
      
      <Form.Item name="logo_url" label="Logo链接">
        <Input placeholder="品牌Logo的URL地址" />
      </Form.Item>
      
      <Form.Item name="visual_style" label="视觉风格">
        <TextArea rows={2} placeholder="品牌视觉设计风格，如：简约现代、复古经典等" />
      </Form.Item>
      
      <Form.Item name="is_active" label="激活状态" valuePropName="checked">
        <Switch checkedChildren="激活" unCheckedChildren="停用" />
      </Form.Item>
    </Form>
  );
};

